import { AddOrEditMatterTeamModal } from "@/components/features/settings/defaults/matter-teams/AddOrEditMatterTeamModal";
import { MatterTeamRowContextMenu } from "@/components/features/settings/defaults/matter-teams/MatterTeamRowContextMenu";
import {
  MattersStyleTableShell,
  useMattersStyleTableColumnDragSensors,
} from "@/components/features/matters/matters-style-table/MattersStyleTableShell";
import { ActionConfirmationModal, ExpandableText, Text } from "@/components/ui";
import { useUser } from "@/components/UserProvider";
import { retrieveMatterSettingsQueryOptions } from "@/lib/data/react-query/matters/matter-settings";
import { useDeleteMatterTeam } from "@/lib/data/react-query/matters/matter-team";
import { serverErrorFallbackText } from "@/lib/data/static/fallbackMessages";
import {
  ACTION_PERMISSION_KEYS,
  ACTION_PERMISSION_VALUES,
} from "@/lib/data/static/permissionOptions";
import { tanstackTableNames } from "@/lib/data/static/tanstack-table-names";
import { type MatterTeam } from "@/lib/types";
import {
  persistTanstackTablePrefs,
  readTanstackTablePrefs,
} from "@/lib/utils/tanstack-table-local-storage";
import { arrayMove } from "@dnd-kit/sortable";
import type { DragEndEvent } from "@dnd-kit/core";
import { ErrorBoundary } from "@sentry/react";
import {
  type ColumnDef,
  type SortingState,
  type VisibilityState,
  functionalUpdate,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { customDateTimeSortingFns } from "@/lib/utils/tableUtils";

const MATTER_TEAM_TABLE_COLUMN_IDS = ["Label", "Description"] as const;

const MATTER_TEAMS_TABLE_LS_KEY: string = tanstackTableNames.matterTeams;

function MatterTeamsTableDangerous({
  isAdding,
  setIsAdding,
}: {
  isAdding: boolean;
  setIsAdding: (open: boolean) => void;
}) {
  const { hasActionPermission } = useUser();

  const hasUpdatePermission = hasActionPermission(
    ACTION_PERMISSION_KEYS.matterTeam,
    ACTION_PERMISSION_VALUES.update,
  );
  const hasDeletePermission = hasActionPermission(
    ACTION_PERMISSION_KEYS.matterTeam,
    ACTION_PERMISSION_VALUES.delete,
  );

  const [isEditing, setIsEditing] = useState(false);
  const [activeTeamId, setActiveTeamId] = useState<string | null>(null);
  const [teamToDelete, setTeamToDelete] = useState<MatterTeam | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const deleteMatterTeam = useDeleteMatterTeam();

  const [sorting, setSorting] = useState<SortingState>(() => {
    const stored = readTanstackTablePrefs(MATTER_TEAMS_TABLE_LS_KEY);
    return stored.sorting ?? [];
  });
  const [pagination, setPagination] = useState(() => {
    const stored = readTanstackTablePrefs(MATTER_TEAMS_TABLE_LS_KEY);
    return {
      pageIndex: 0,
      pageSize: stored.pagination?.pageSize ?? 10,
    };
  });
  const [columnOrder, setColumnOrder] = useState<string[]>(() => {
    const stored = readTanstackTablePrefs(MATTER_TEAMS_TABLE_LS_KEY);
    const ids = [...MATTER_TEAM_TABLE_COLUMN_IDS];
    const order = stored.columnOrder;
    if (order?.length) {
      return [...order, ...ids.filter((id) => !order.includes(id))];
    }
    return ids;
  });
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(
    () => {
      const stored = readTanstackTablePrefs(MATTER_TEAMS_TABLE_LS_KEY);
      return {
        ...stored.columnVisibility,
        Label: true,
      };
    },
  );

  const {
    data: matterSettingsData,
    isLoading: isMatterSettingsLoading,
    isError: isMatterSettingsError,
  } = useQuery(retrieveMatterSettingsQueryOptions());

  const matterTeams = useMemo(
    () => matterSettingsData?.data.teams ?? [],
    [matterSettingsData?.data.teams],
  );

  const filteredRows = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return matterTeams;
    return matterTeams.filter((row) => {
      const label = row.label.toLowerCase();
      const description = (row.description ?? "").toLowerCase();
      return label.includes(q) || description.includes(q);
    });
  }, [matterTeams, searchQuery]);

  const columns = useMemo<ColumnDef<MatterTeam>[]>(
    () => [
      {
        id: "Label",
        accessorFn: (row) => row.label,
        header: "Label",
        enableHiding: false,
        cell: ({ row }) => (
          <Text variant="body" className="font-semibold">
            {row.original.label}
          </Text>
        ),
        sortingFn: "alphanumeric",
      },
      {
        id: "Description",
        accessorFn: (row) => row.description ?? "",
        header: "Description",
        cell: ({ row }) => {
          const value = row.original.description;
          if (!value) {
            return <Text variant="subHeading">-</Text>;
          }
          return (
            <ExpandableText
              text={String(value)}
              maxLines={3}
              className="text-[13px] leading-5"
            />
          );
        },
        sortingFn: "alphanumeric",
      },
    ],
    [],
  );

  const table = useReactTable({
    data: filteredRows,
    columns,
    autoResetPageIndex: true,
    state: {
      sorting,
      columnOrder,
      columnVisibility,
      pagination,
    },
    onSortingChange: (updater) => {
      setSorting((prev) => {
        const next = functionalUpdate(updater, prev);
        persistTanstackTablePrefs(MATTER_TEAMS_TABLE_LS_KEY, {
          sorting: next,
        });
        return next;
      });
    },
    onPaginationChange: (updater) => {
      setPagination((prev) => {
        const next = functionalUpdate(updater, prev);
        persistTanstackTablePrefs(MATTER_TEAMS_TABLE_LS_KEY, {
          pagination: { pageSize: next.pageSize },
        });
        return next;
      });
    },
    onColumnOrderChange: (updater) => {
      setColumnOrder((prev) => {
        const next = functionalUpdate(updater, prev);
        persistTanstackTablePrefs(MATTER_TEAMS_TABLE_LS_KEY, {
          columnOrder: next,
        });
        return next;
      });
    },
    onColumnVisibilityChange: (updater) => {
      setColumnVisibility((prev) => {
        const next = functionalUpdate(updater, prev);
        const merged = { ...next, Label: true };
        persistTanstackTablePrefs(MATTER_TEAMS_TABLE_LS_KEY, {
          columnVisibility: merged,
        });
        return merged;
      });
    },
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    sortingFns: customDateTimeSortingFns,
  });

  const sensors = useMattersStyleTableColumnDragSensors();

  const handleColumnDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    setColumnOrder((prev) => {
      const oldIndex = prev.indexOf(active.id as string);
      const newIndex = prev.indexOf(over.id as string);
      if (oldIndex < 0 || newIndex < 0) return prev;
      const next = arrayMove(prev, oldIndex, newIndex);
      persistTanstackTablePrefs(MATTER_TEAMS_TABLE_LS_KEY, {
        columnOrder: next,
      });
      return next;
    });
  };

  const activeMatterTeam = matterTeams.find((team) => team.id === activeTeamId);

  const handleDeleteMatterTeam = async () => {
    if (!teamToDelete) return;

    await deleteMatterTeam.mutateAsync({ id: teamToDelete.id });
    setTeamToDelete(null);
  };

  return (
    <div>
      <MattersStyleTableShell
        table={table}
        columnOrder={columnOrder}
        columnIdCountBaseline={MATTER_TEAM_TABLE_COLUMN_IDS.length}
        onColumnDragEnd={handleColumnDragEnd}
        sensors={sensors}
        search={{
          value: searchQuery,
          onChange: (value) => {
            setSearchQuery(value);
            setPagination((p) => ({ ...p, pageIndex: 0 }));
          },
          placeholder: "Search teams...",
        }}
        isLoading={isMatterSettingsLoading}
        isError={isMatterSettingsError}
        errorMessage={serverErrorFallbackText}
        emptyMessage="No matter teams found."
        totalFilteredRowCount={filteredRows.length}
        entityNamePlural="teams"
        sorting={sorting}
        tableProps={{
          "data-testid": tanstackTableNames.matterTeams,
          "data-table-name": tanstackTableNames.matterTeams,
        }}
        getTableRowProps={(row) => ({
          "data-testid": "table-row",
          className: "cursor-pointer",
          onClick: () => {
            setActiveTeamId(row.original.id);
            setIsEditing(true);
          },
        })}
        renderContextMenu={(row, trigger) => (
          <MatterTeamRowContextMenu
            team={row.original}
            trigger={trigger}
            canEdit={hasUpdatePermission}
            canDelete={hasDeletePermission}
            onEdit={() => {
              setActiveTeamId(row.original.id);
              setIsEditing(true);
            }}
            onDelete={() => {
              setTeamToDelete(row.original);
            }}
          />
        )}
      />

      <AddOrEditMatterTeamModal
        editing={false}
        open={isAdding}
        setOpen={setIsAdding}
      />
      {activeTeamId && activeMatterTeam ? (
        <AddOrEditMatterTeamModal
          activeMatterTeam={activeMatterTeam}
          isReadOnly={!hasUpdatePermission}
          editing
          open={isEditing}
          setOpen={setIsEditing}
          setActiveMatterTeamId={setActiveTeamId}
        />
      ) : null}

      <ActionConfirmationModal
        open={Boolean(teamToDelete)}
        setOpen={(open) => {
          if (!open) setTeamToDelete(null);
        }}
        title="Delete matter team"
        description={`Are you sure you want to delete "${teamToDelete?.label}"? This action cannot be undone.`}
        buttonText="Delete"
        afterActionFn={handleDeleteMatterTeam}
      />
    </div>
  );
}

export function MatterTeamsTable({
  isAdding,
  setIsAdding,
}: {
  isAdding: boolean;
  setIsAdding: (open: boolean) => void;
}) {
  return (
    <ErrorBoundary
      fallback={<Text variant="subHeading">{serverErrorFallbackText}</Text>}
    >
      <MatterTeamsTableDangerous
        isAdding={isAdding}
        setIsAdding={setIsAdding}
      />
    </ErrorBoundary>
  );
}
